/* global Engage, jQuery, google */

/**
 * @description Initializes the core map module for the Engage Locator.
 * @param  {jQuery} $ - A reference to the global jQuery namespace.
 */
void (function initializeLocatorMap($) {
	'use strict';

	window.Engage = window.Engage || {};
	Engage.Locator = Engage.Locator || {};

	if (!Engage.Locator.Messaging) {
		const retryDelay = 20;
		setTimeout(() => initializeLocatorMap($), retryDelay);
		return;
	}

	/**
	 * @namespace Window.Engage.Locator.Map
	 */
	Engage.Locator.Map = Engage.Locator.Map || {};

	/**
	 * @function initialize
	 * @description Initializes the Google Map and Geolocation library used by the Engage Locator.
	 * @example
	 * Engage.Locator.Map();
	 * @memberof Window.Engage.Locator.Map
	 */
	Engage.Locator.Map.initialize = () => {
		const apiKey = Engage.Locator.Settings.GoogleMapsApiKey;
		const $loadingIndicator = $('.engage-locator__loading');
		const geocodeApiEndPoint =
			'https://maps.googleapis.com/maps/api/geocode/json';
		const messaging = Engage.Locator.Messaging;

		/**
		 * @private
		 * @function publishZipCodeValidationResult
		 * @description Published a {Window.Engage.Locator.Messaging.messageTypes.zipCodeValidationResult} message
		 * with the payload provided by the {data} parameter.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} status - The message type status to publish to
		 * {Window.Engage.Locator.Messaging.messageTypes.zipCodeValidationResult}
		 * @param {Object} data - The payload data to publish with the message.
		 */
		function publishZipCodeValidationResult(status, data) {
			const payload = {
				status,
				data,
			};
			messaging.publish(
				messaging.messageTypes.zipCodeValidationResult,
				payload,
			);
		}

		/**
		 * @private
		 * @function geocodeAddressPromise
		 * @description Geocode the provided address using the Google API.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} address - The address to geocode with the Google API.
		 * @returns {jQuery} A jQuery promise created by making a AJAX request to the Google API.
		 */
		function geocodeAddressPromise(address) {
			if (Engage.Locator.Map.addressProcessor) {
				address = Engage.Locator.Map.addressProcessor(address);
			}
			return $.ajax({
				type: 'GET',
				url: `${geocodeApiEndPoint}?address=${address}&key=${apiKey}`,
				dataType: 'json',
			});
		}

		/**
		 * @private
		 * @name validateZipCodeSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.validateZipCode} messages.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.validateZipCode,
			(messageType, payload) => {
				if (
					messageType !== messaging.messageTypes.validateZipCode ||
					!payload.address
				) {
					return;
				}

				geocodeAddressPromise(payload.address)
					.done((response) => {
						if (!response) {
							publishZipCodeValidationResult('ERROR', {
								status: 200,
								error: 'Could not find your location from the supplied address.',
								address: payload.address,
							});
							return;
						}

						if (response.status !== 'OK') {
							let error = '';
							switch (response.status) {
								case 'ZERO_RESULTS':
									publishZipCodeValidationResult(
										'NO_MATCHES_FOUND',
										{ address: payload.address },
									);
									return;
								case 'OVER_QUERY_LIMIT':
									error =
										'Attempting to find a location after exceeding your quota.';
									break;
								case 'REQUEST_DENIED':
									error =
										'The request to find your location has been denied.';
									break;
								case 'INVALID_REQUEST':
									error =
										'Attempting to find a location with an invalid request.';
									break;
								case 'UNKNOWN_ERROR':
									error =
										'An unknown error occurred when trying to find your location.';
									break;
								default:
									error =
										'Could not find your location from the supplied address.'; // eslint-disable-line max-len
							}
							publishZipCodeValidationResult('ERROR', {
								status: 200,
								error,
								address: payload.address,
							});
							return;
						}

						const matches = $.map(
							response.results,
							(locationMatch) => ({
								name: locationMatch.formatted_address,
								latitude: locationMatch.geometry.location.lat,
								longitude: locationMatch.geometry.location.lng,
							}),
						);

						if (matches.length === 1) {
							publishZipCodeValidationResult(
								'EXACT_MATCH_FOUND',
								{
									match: matches[0],
									address: payload.address,
								},
							);
							return;
						}

						publishZipCodeValidationResult(
							'MULTIPLE_MATCHES_FOUND',
							{ matches, address: payload.address },
						);
					})
					.fail((_, status, error) => {
						publishZipCodeValidationResult('ERROR', {
							status,
							error,
							address: payload.address,
						});
					});
			},
		);

		const defaultZoom = 4;
		const minimumZoomForLocationsRefresh = 7;
		const defaultLat = 39.5;
		const defaultLng = -98.35;
		const oneSecond = 1000;

		/**
		 * @private
		 * @name mapOptions
		 * @description Defines all the static configuration options for the map.
		 * @memberof Window.Engage.Locator.Map
		 * @property {number} zoom - The default zoom level for the map.
		 * @property {Object} center - An object defining the center latitude and longitude for the map.
		 * @property {bool} panControl - Defines whether to show the pan controls.
		 * @property {Object} panControlOptions - An object defining the position for the pan controls if shown.
		 * @property {bool} zoomControl - Defines whether to show the zoom controls.
		 * @property {Object} zoomControlOptions - An object defining the style and position for hte zoom controls
		 * if shown.
		 * @property {bool} scaleControl - Defines whether to show the scale controls.
		 */
		const mapOptions = {
			zoom: defaultZoom,
			center: new google.maps.LatLng(defaultLat, defaultLng),
			panControl: false,
			panControlOptions: {
				position: google.maps.ControlPosition.BOTTOM_LEFT,
			},
			zoomControl: true,
			zoomControlOptions: {
				style: google.maps.ZoomControlStyle.LARGE,
				position: google.maps.ControlPosition.RIGHT_CENTER,
			},
			scaleControl: false,
		};

		/**
		 * @private
		 * @const {Object} zeMap - Defines the Google Maps object.
		 * @memberof Window.Engage.Locator.Map
		 */
		const zeMap = new google.maps.Map(
			document.getElementById('LocatorMap'),
			mapOptions,
		);

		let markerArr = [];

		/**
		 * @private
		 * @function publishBoxUpdate
		 * @description Publishes a message with the current map bounds as the payload.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} messageType - The message to publish the update map bounds.
		 */
		function publishBoxUpdate(messageType) {
			const bounds = zeMap.getBounds();
			const northEastLatitude = bounds.getNorthEast().lat();
			const northEastLongitude = bounds.getNorthEast().lng();
			const southWestLatitude = bounds.getSouthWest().lat();
			const southWestLongitude = bounds.getSouthWest().lng();
			messaging.publish(messageType, {
				locationTopRight: {
					latitude: northEastLatitude,
					longitude: northEastLongitude,
				},
				locationBottomLeft: {
					latitude: southWestLatitude,
					longitude: southWestLongitude,
				},
				center: {
					latitude: zeMap.getCenter().lat(),
					longitude: zeMap.getCenter().lng(),
				},
			});
		}

		/**
		 * @private
		 * @const {number} xOffset - The default X coordinate offset for the default map marker label.
		 * @memberof Window.Engage.Locator.Map
		 */
		const xOffset = 105;

		/**
		 * @private
		 * @const {number} yOffset - The default Y coordinate offset for the default map marker label.
		 * @memberof Window.Engage.Locator.Map
		 */
		const yOffset = 110;

		/**
		 * @private
		 * @const {Object} defaultMarkerIcon - The default google maps marker icon to use for the map POI's.
		 * @memberof Window.Engage.Locator.Map
		 */
		const defaultMarkerIcon = {
			path: 'M72.1,214H7a4,4,0,0,1-4-4V7A4,4,0,0,1,7,3H210a4,4,0,0,1,4,4V210a4,4,0,0,1-4,4H145.18l-36.54,61.9Z',
			fillColor: '#5ea9ce',
			fillOpacity: 1,
			scale: 0.15,
			labelOrigin: new google.maps.Point(xOffset, yOffset),
		};

		if (!Engage.Locator.Map.getMapMarkerIcon) {
			/**
			 * @function getMapMarkerIcon
			 * @description Creates the object for the map maker to send to google maps.
			 * @memberof Window.Engage.Locator.Map
			 * @param  {Object} coords - The latitude and longitude coordinates for the map marker.
			 * @param  {string} content - The optional content to display in the InfoWindow.
			 * @param  {number} sortPosition - The label that will be rendered on the map.
			 * @returns {Object} The marker icon to send over to google maps.
			 */
			Engage.Locator.Map.getMapMarkerIcon = (
				coords,
				content,
				sortPosition,
			) => {
				if (sortPosition > -1) {
					return defaultMarkerIcon;
				}

				return $.extend({}, defaultMarkerIcon, {
					fillColor: '#ff0000',
				});
			};
		}

		/**
		 * @private
		 * @function createMapMarker
		 * @description Create a Google.maps.Marker and optionally attaches a Google.maps.InfoWindow if content
		 * is supplied.
		 * @memberof Window.Engage.Locator.Map
		 * @param  {Object} coords - The latitude and longitude coordinates for the map marker.
		 * @param  {string} zeContent - The optional content to display in the InfoWindow.
		 * @param  {number} sortPosition - The label that will be rendered on the map.
		 * @param  {string} locationName - The name of the location.
		 * @returns {Object} An object containing the Google.maps.Marker and optionally attaches a
		 * Google.maps.InfoWindow if content is supplied.
		 */
		function createMapMarker(
			coords,
			zeContent,
			sortPosition,
			locationName,
		) {
			const self = {};
			let icon = defaultMarkerIcon;

			if (Engage.Locator.Map.getMapMarkerIcon) {
				const customIcon = Engage.Locator.Map.getMapMarkerIcon(
					coords,
					zeContent,
					sortPosition,
					locationName,
				);
				if (customIcon) {
					icon = customIcon;
				}
			}

			self.marker = new google.maps.Marker({
				position: coords,
				map: zeMap,
				title: locationName,
				label:
					sortPosition > -1
						? {
								color: '#fff',
								fontSize: '12px',
								fontWeight: '600',
								text: sortPosition.toString(),
						  }
						: null,
				icon,
			});

			if (zeContent) {
				self.infoWindow = new google.maps.InfoWindow({
					content: zeContent,
				});

				self.marker.addListener('click', () => {
					self.infoWindow.open(zeMap, self.marker);
					for (let i = 0; i < markerArr.length; i++) {
						if (markerArr[i] !== self) {
							markerArr[i].infoWindow.close(
								zeMap,
								markerArr[i].marker,
							);
						}
					}
				});
			}

			if (Engage.Locator.Map.customizeMapMarker) {
				return Engage.Locator.Map.customizeMapMarker(self) ?? self;
			}

			return self;
		}

		let processingLocations = false;
		let receivedNewLocations = false;

		let dragendTimer;

		/**
		 * @global
		 * @description Defines the structure of the callback for all Google Map API callbacks.
		 * @callback GoogleMapCallback
		 */

		/**
		 * @private
		 * @name dragEndEventHandler
		 * @description Handles all dragend events from the Google Map.
		 * @memberof Window.Engage.Locator.Map
		 * @param {Object} zeMap - The instance of the Google Map.
		 * @param {string} eventName - The event name to bind to the map.
		 * @param {GoogleMapCallback} callBack - The callback to handle the event that was passed into {eventName}.
		 */
		google.maps.event.addListener(zeMap, 'dragend', () => {
			window.clearTimeout(dragendTimer);

			if (processingLocations) {
				return;
			}

			if (receivedNewLocations) {
				receivedNewLocations = false;
				return;
			}

			dragendTimer = window.setTimeout(() => {
				const newZoomLevel = zeMap.getZoom();
				if (newZoomLevel >= minimumZoomForLocationsRefresh) {
					processingLocations = true;
					publishBoxUpdate(messaging.messageTypes.boundingBoxUpdate);
					processingLocations = false;
				}
			}, oneSecond);
		});

		let zoomChangedTimer;

		/**
		 * @private
		 * @name zoomChangeEventHandler
		 * @description Handles all dragend events from the Google Map.
		 * @memberof Window.Engage.Locator.Map
		 * @param {Object} zeMap - The instance of the Google Map.
		 * @param {string} eventName - The event name to bind to the map.
		 * @param {GoogleMapCallback} callBack - The callback to handle the event that was passed into {eventName}.
		 */
		google.maps.event.addListener(zeMap, 'zoom_changed', () => {
			window.clearTimeout(zoomChangedTimer);

			if (processingLocations) {
				return;
			}

			if (receivedNewLocations) {
				receivedNewLocations = false;
				return;
			}

			dragendTimer = window.setTimeout(() => {
				const newZoomLevel = zeMap.getZoom();
				if (newZoomLevel >= minimumZoomForLocationsRefresh) {
					processingLocations = true;
					publishBoxUpdate(messaging.messageTypes.boundingBoxUpdate);
					processingLocations = false;
				}
			}, oneSecond + oneSecond);
		});

		function addMarkerForZipCodeValidationResult(
			messageType,
			{ data = {} },
		) {
			if (
				messageType !==
					messaging.messageTypes.zipCodeValidationResult ||
				!data
			) {
				return;
			}

			if (!data.match) {
				return;
			}

			const match = data.match;
			const content = '';
			const sortPosition = -1;
			const locationName = match.name || data.address;
			createMapMarker(
				{ lat: match.latitude, lng: match.longitude },
				content,
				sortPosition,
				locationName,
			);
		}

		/**
		 * @private
		 * @const {string[]} messageTypesThatCauseSearchUpdates - An array of all messages types that mark a search
		 * action has been completed.
		 * @memberof Window.Engage.Locator.Map
		 */
		const messageTypesThatCauseSearchUpdates = [
			messaging.messageTypes.locationUpdate,
			messaging.messageTypes.newQueryFilters,
			messaging.messageTypes.boundingBoxUpdate,
			messaging.messageTypes.showLoading,
		];

		/**
		 * @private
		 * @name messageTypesThatCauseSearchUpdatesSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Subscribe to all messages in the Engage Locator, only handle messages
		 * that exist in the {messageTypesThatCauseSearchUpdates} member.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(messaging.locatorMessagePrefix, (messageType) => {
			if (
				messageTypesThatCauseSearchUpdates.indexOf(messageType) === -1
			) {
				return;
			}

			$loadingIndicator.removeClass('engage-locator__loading__hidden');
			$loadingIndicator.addClass('engage-locator__loading__show');
		});

		/**
		 * @private
		 * @name resetSearchFromSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.resetSearchFrom} messages.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(messaging.messageTypes.resetSearchFrom, () => {
			zeMap.setCenter(new google.maps.LatLng(defaultLat, defaultLng));
			zeMap.setZoom(defaultZoom);
		});

		if (Engage.Locator.Settings.ShowSearchLocationPin) {
			messaging.subscribe(
				messaging.messageTypes.zipCodeValidationResult,
				addMarkerForZipCodeValidationResult,
			);
		}

		/**
		 * @private
		 * @name newLocationsSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.newLocations} messages.
		 * @memberof Window.Engage.Locator.Map
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.newLocations,
			(_, { type, locations }) => {
				if (processingLocations) {
					return;
				}

				for (let i = 0; i < markerArr.length; i++) {
					markerArr[i].marker.setMap(null);
				}
				markerArr = [];

				const bounds = new google.maps.LatLngBounds();
				if (locations.length > 0) {
					for (let i = 0; i < locations.length; i++) {
						receivedNewLocations = true;
						markerArr.push(
							createMapMarker(
								{
									lat: locations[i].latitude,
									lng: locations[i].longitude,
								},
								locations[i].template,
								locations[i].order,
								locations[i].name,
							),
						);
						bounds.extend(markerArr[i].marker.getPosition());
					}

					if (type !== 'BoundingBoxSearch') {
						zeMap.fitBounds(bounds);
					}
					receivedNewLocations = false;
				}

				$loadingIndicator.removeClass('engage-locator__loading__show');
				$loadingIndicator.addClass('engage-locator__loading__hidden');
			},
		);
	};
})(jQuery);
